import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import TripMap from '../TripMap';
import { API_BASE } from '../../utils/api';

const TicketDetailEnhanced = ({ ticket, onBack, onStatusUpdate }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [showEscalateForm, setShowEscalateForm] = useState(false);
  const [escalationData, setEscalationData] = useState({
    reason: '',
    escalatedTo: ''
  });
  const [tripDetails, setTripDetails] = useState(null);
  const [loadingTrip, setLoadingTrip] = useState(false);

  useEffect(() => {
    fetchComments();
    if (ticket.tripId && ticket.type === 'trip_related') {
      fetchTripDetails();
    }
  }, [ticket.ticketId, ticket.tripId]);

  const fetchComments = async () => {
    try {
      const response = await fetch(`${API_BASE}/tickets/${ticket.ticketId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setComments(data.data.comments || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchTripDetails = async () => {
    if (!ticket.tripId) return;
    
    setLoadingTrip(true);
    try {
      const response = await fetch(`${API_BASE}/trips/${ticket.tripId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTripDetails(data.data);
      }
    } catch (error) {
      console.error('Error fetching trip details:', error);
    } finally {
      setLoadingTrip(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('comment', newComment);
      formData.append('commentType', 'public');

      // Add attachments
      attachments.forEach(file => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${API_BASE}/tickets/${ticket.ticketId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setComments(prev => [...prev, data.data]);
        setNewComment('');
        setAttachments([]);
        toast.success('Comment added successfully');
      } else {
        toast.error(data.message || 'Error adding comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Error adding comment');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(files);
  };

  const handleEscalate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/tickets/${ticket.ticketId}/escalate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(escalationData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Ticket escalated successfully');
        setShowEscalateForm(false);
        onStatusUpdate(ticket.ticketId, 'escalated');
      } else {
        toast.error(data.message || 'Error escalating ticket');
      }
    } catch (error) {
      console.error('Error escalating ticket:', error);
      toast.error('Error escalating ticket');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      escalated: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canEscalate = user.role === 'SUPER_ADMIN' || user.role === 'regional_admin' || user.role === 'regional_employee';
  const canUpdateStatus = user.role === 'SUPER_ADMIN' || user.role === 'regional_admin' || user.role === 'regional_employee';
  const canUpdatePriority = user.role === 'SUPER_ADMIN' || user.role === 'regional_admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Back to Tickets
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Ticket Details</h1>
        </div>
        <div className="flex space-x-2">
          {canEscalate && ticket.status !== 'escalated' && (
            <button
              onClick={() => setShowEscalateForm(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Escalate
            </button>
          )}
        </div>
      </div>

      {/* Trip Details Section - Only show for trip-related tickets */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Trip Information</h2>
        {loadingTrip ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading trip details...</p>
          </div>
        ) : tripDetails ? (
          <>
            {/* Trip Map */}
            <div className="mb-6">
              <h3 className="font-medium text-gray-700 mb-3">Trip Route Map</h3>
              <TripMap tripDetails={tripDetails} height="400px" />
            </div>

            {/* Trip Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Trip Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Trip Details</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Trip ID:</span>
                    <span className="font-medium">{tripDetails.tripId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      tripDetails.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                      tripDetails.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {tripDetails.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fare:</span>
                    <span className="font-medium">₹{tripDetails.fare || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Method:</span>
                    <span className="font-medium">{tripDetails.paymentMethod || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Start Time:</span>
                    <span className="font-medium">{tripDetails.startTime ? formatDate(tripDetails.startTime) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">End Time:</span>
                    <span className="font-medium">{tripDetails.endTime ? formatDate(tripDetails.endTime) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Distance:</span>
                    <span className="font-medium">{tripDetails.finalDistance ? `${tripDetails.finalDistance.toFixed(2)} km` : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-medium">{tripDetails.finalDuration ? `${tripDetails.finalDuration} mins` : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Stops:</span>
                    <span className="font-medium">{tripDetails.stopsCount || 0}</span>
                  </div>
                  {tripDetails.otp && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trip OTP:</span>
                      <span className="font-mono text-xs">{tripDetails.otp}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Route Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Route Information</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-gray-900">Pickup Location</p>
                      <p className="text-gray-600 text-sm">{tripDetails.startLocation || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-gray-900">Destination</p>
                      <p className="text-gray-600 text-sm">{tripDetails.endLocation || 'N/A'}</p>
                    </div>
                  </div>
                  {tripDetails.stops && tripDetails.stops.length > 0 && (
                    <div className="mt-4">
                      <p className="font-medium text-gray-900 mb-2">Stops:</p>
                      <div className="space-y-2">
                        {tripDetails.stops.map((stop, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className={`w-3 h-3 rounded-full mt-1 ${
                              stop.name === 'Pickup Point' ? 'bg-blue-500' :
                              stop.name === 'Drop Point' ? 'bg-red-500' :
                              'bg-yellow-500'
                            }`}></div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{stop.name}</p>
                              <p className="text-gray-600 text-xs">{stop.address}</p>
                              {stop.waitingTime > 0 && (
                                <p className="text-orange-600 text-xs">Wait: {stop.waitingTime} mins</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Passenger and Driver Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Passenger Details */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-3">Passenger Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-blue-700">Name:</span>
                    <span className="font-medium">{tripDetails.passengerId?.name || tripDetails.bookingForName || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Phone:</span>
                    <span className="font-medium">{tripDetails.passengerId?.phone || tripDetails.bookingForPhone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Email:</span>
                    <span className="font-medium">{tripDetails.passengerId?.email || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Total Trips:</span>
                    <span className="font-medium">{tripDetails.passengerId?.totalTrips || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Driver Details */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900 mb-3">Driver Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-green-700">Name:</span>
                    <span className="font-medium">{tripDetails.driverId?.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Phone:</span>
                    <span className="font-medium">{tripDetails.driverId?.phone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">License:</span>
                    <span className="font-medium">{tripDetails.driverId?.licenseNo || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Vehicle:</span>
                    <span className="font-medium">{tripDetails.driverId?.vehicleDetails?.model || 'N/A'}</span>
                  </div>
                  {tripDetails.driverId?.ratingData && (
                    <div className="flex justify-between">
                      <span className="text-green-700">Rating:</span>
                      <span className="font-medium">{tripDetails.driverId.ratingData.rating || 'N/A'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Regional Office Information */}
            {tripDetails.regionalOffice && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-medium text-purple-900 mb-3">Regional Office</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-purple-700">Office Name:</span>
                    <span className="font-medium">{tripDetails.regionalOffice.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-700">Address:</span>
                    <span className="font-medium">{tripDetails.regionalOffice.address || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-700">Phone:</span>
                    <span className="font-medium">{tripDetails.regionalOffice.phone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-700">Email:</span>
                    <span className="font-medium">{tripDetails.regionalOffice.email || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <p className="text-gray-600">No trip details available</p>
            <p className="text-sm text-gray-500 mt-2">
              Trip information is not available for this ticket
            </p>
          </div>
        )}
      </div>

      {/* Ticket Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-semibold mb-4">{ticket.title}</h2>
            <div className="space-y-3">
              <div>
                <span className="font-medium text-gray-700">Ticket ID:</span>
                <span className="ml-2 text-gray-900">{ticket.ticketId}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                  {ticket.status.replace('_', ' ')}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Priority:</span>
                <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(ticket.priority)}`}>
                  {ticket.priority}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Type:</span>
                <span className="ml-2 text-gray-900">{ticket.type.replace('_', ' ')}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Category:</span>
                <span className="ml-2 text-gray-900">{ticket.categoryId?.name || 'N/A'}</span>
              </div>
              {ticket.subcategoryId && (
                <div>
                  <span className="font-medium text-gray-700">Subcategory:</span>
                  <span className="ml-2 text-gray-900">{ticket.subcategoryId?.name || 'N/A'}</span>
                </div>
              )}
              {ticket.tripId && (
                <div>
                  <span className="font-medium text-gray-700">Trip ID:</span>
                  <span className="ml-2 text-gray-900">{ticket.tripId}</span>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700">Raised By:</span>
                <span className="ml-2 text-gray-900">{ticket.raisedBy?.userName || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Created:</span>
                <span className="ml-2 text-gray-900">{formatDate(ticket.createdAt)}</span>
              </div>
            </div>
          </div>
          <div>
            <h3 className="font-medium text-gray-700 mb-2">Description</h3>
            <p className="text-gray-900 whitespace-pre-wrap">{ticket.description}</p>
            
            {ticket.tags && ticket.tags.length > 0 && (
              <div className="mt-4">
                <h3 className="font-medium text-gray-700 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {ticket.tags.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status and Priority Update */}
        {(canUpdateStatus || canUpdatePriority) && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="font-medium text-gray-700 mb-2">Update Ticket</h3>
            <div className="space-y-4">
              {canUpdateStatus && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={ticket.status}
                    onChange={(e) => onStatusUpdate(ticket.ticketId, { status: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                    <option value="escalated">Escalated</option>
                  </select>
                </div>
              )}
              {canUpdatePriority && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={ticket.priority}
                    onChange={(e) => onStatusUpdate(ticket.ticketId, { priority: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Resolution Details */}
        {ticket.resolution && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="font-medium text-gray-700 mb-2">Resolution</h3>
            <div className="bg-green-50 p-4 rounded-md">
              <p className="text-green-800">{ticket.resolution.summary}</p>
              {ticket.resolution.actionTaken && (
                <p className="text-green-700 mt-2">
                  <strong>Action Taken:</strong> {ticket.resolution.actionTaken}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Comments Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Comments</h2>
        
        {/* Add Comment Form */}
        <form onSubmit={handleAddComment} className="mb-6">
          <div className="space-y-4">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <div className="flex items-center justify-between">
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
                className="text-sm"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add Comment'}
              </button>
            </div>
          </div>
        </form>

        {/* Comments List */}
        <div className="space-y-4">
          {comments.map((comment, index) => (
            <div key={index} className="border-l-4 border-blue-500 pl-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className="font-medium text-gray-900">
                    {comment.addedBy.userName || comment.addedBy.userType}
                  </span>
                  <span className="text-sm text-gray-500 ml-2">
                    {formatDate(comment.createdAt)}
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  comment.commentType === 'internal' 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : comment.commentType === 'system'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {comment.commentType}
                </span>
              </div>
              <p className="text-gray-900 whitespace-pre-wrap">{comment.comment}</p>
              
              {/* Attachments */}
              {comment.attachments && comment.attachments.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Attachments:</h4>
                  <div className="flex flex-wrap gap-2">
                    {comment.attachments.map((attachment, idx) => (
                      <a
                        key={idx}
                        href={attachment.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200"
                      >
                        📎 {attachment.fileName}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Escalation Modal */}
      {showEscalateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Escalate Ticket</h3>
            <form onSubmit={handleEscalate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Escalation *
                </label>
                <textarea
                  value={escalationData.reason}
                  onChange={(e) => setEscalationData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEscalateForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? 'Escalating...' : 'Escalate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailEnhanced; 